import { useEffect, useState } from "react";
import {
  FormHelperText,
  Grid,
  CircularProgress,
  FormControl,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  TextField,
} from "@mui/material";
import CustomInput from "../utils/Input";
import ClientDropdown from "./ClientDropdown";
import GlobalDropdownField from "../utils/GlobalDropdownField";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PDF from "../assets/icons/PDF.png";
import JPG from "../assets/icons/JPG.png";
import XLS from "../assets/icons/XLS.png";
import DOC from "../assets/icons/DOC.png";
import { DeleteOutlineOutlined } from "@mui/icons-material";
import CustomButton from "../utils/Button";
import DownloadIcon from "../assets/icons/Download";
import LocationsTablesDialog from "./LocationsTablesModal.jsx";
import AllLocationMapModal from "./AllLocationMapModal.jsx";
import CalculateStoreDistance from "./CalculateStoreDistance.jsx";
import MapboxDistance from "./MapboxDistance.jsx";
import NextBillionDistance from "./NextBillionDistance.jsx";
import styles from "../../styles/vendorPurchaseOrder.module.css";
import PNG from "../assets/icons/PNG.png";
import { downloadExcelForInputLocations } from "../../components/AdminportalFolder/RFP/downloadExcelSheetForInputLocations";

const EnquiryHeader = ({
  formData,
  categories,
  productTypes,
  loadingProductTypes,
  onCategoryChange,
  onProductTypeChange,
  handleRemoveDocument,
  setExcelLoading,
  setFormData,
  setSnackbar,
  operation,
  errors,
  handleInputChange,
  validateForm,
  saveButtonClicked,
  selectedCampaign,
  organizationList,
  team,
  PRODUCT_TYPES,
  setIsLocationModal,
  isProduct,
  onAddNew,
  isLocationModal,
  locationMapModal,
  setLocationMapModal,
  distanceModal,
  setDistanceModal,
  locationFileInputRef,
  setProductsLocation,
  handleFileUploadForLocations,
  setJobLocations,
  handleOpenModal,
  isJobLocation,
  excelLoading,
  handleIconClick,
  LocationsTablesModal,
  regions = [],
}) => {
  const isViewMode = operation === "view";
  const isEditable = operation === "edit" || operation === "duplicate";
  const defaultCostCalculator = {
    materialCostAmount: "",
    materialCostArea: "",
    recceCostAmount: "",
    recceCostArea: "",
    remoteLocationCostAmount: "",
    remoteLocationCostArea: "",
    smallOrderSizeAmount: "",
    smallOrderSizeArea: "",
    installationCostAmount: "",
    installationCostArea: "",
    marginValue: "",
    bomUploaded: "",
    totalCostOfProduct: "",
    regionWiseCosts: [],
  };

  const ensureCostCalculatorForAllProducts = (products = []) => {
    const updatedProducts = products.map((product) => ({
      ...product,
      costCalculator: product.costCalculator || defaultCostCalculator,
    }));
    return updatedProducts;
  };

  useEffect(() => {
    if (formData.productType && formData.productType.length > 0) {
      const updatedProducts = ensureCostCalculatorForAllProducts(
        formData.products,
      );
      setFormData((prev) => ({
        ...prev,
        products: updatedProducts,
      }));
    }
  }, [formData.productType]);

  const handleCostChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedProducts = [...prev.products];

      updatedProducts[index] = {
        ...updatedProducts[index],
        costCalculator: {
          ...defaultCostCalculator,
          ...updatedProducts[index].costCalculator,
          [field]: Number(value),
        },
      };

      return {
        ...prev,
        products: updatedProducts,
      };
    });
  };
  const handleRegionCostChange = (index, region, value) => {
    setFormData((prev) => {
      const products = [...prev.products];
      const cc = {
        ...defaultCostCalculator,
        ...products[index].costCalculator,
      };
      const list = [...(cc.regionWiseCosts || [])];
      const i = list.findIndex(
        (r) => String(r.regionName) === String(region.regionName),
      );
      const entry = {
        regionName: region.regionName,
        totalCostOfProduct: value, // raw string while typing so "12." stays typeable
      };
      if (i >= 0) list[i] = entry;
      else list.push(entry);
      products[index] = {
        ...products[index],
        costCalculator: { ...cc, regionWiseCosts: list },
      };
      return { ...prev, products };
    });
  };

  const removeRegionCost = (index, region) => {
    setFormData((prev) => {
      const products = [...prev.products];
      const cc = products[index].costCalculator;
      products[index] = {
        ...products[index],
        costCalculator: {
          ...cc,
          regionWiseCosts: (cc.regionWiseCosts || []).filter(
            (r) => String(r.regionName) !== String(region.regionName),
          ),
        },
      };
      return { ...prev, products };
    });
  };

  // utils/costHelpers.js
  const isBlank = (v) => v === "" || v === null || v === undefined;

  /** Cost of a product for a region. Falls back to the legacy single cost. */
  const getRegionCost = (product, regionName) => {
    const cc = product?.costCalculator || {};
    const hit = (cc.regionWiseCosts || []).find(
      (r) => String(r.regionName) === String(regionName),
    );
    if (hit && !isBlank(hit.totalCostOfProduct))
      return Number(hit.totalCostOfProduct);
    return isBlank(cc.totalCostOfProduct)
      ? null
      : Number(cc.totalCostOfProduct);
  };

  const hasLocationData = isJobLocation && isJobLocation.length > 0;

  const downloadExistingLocations = () => {
    if (!hasLocationData) {
      setSnackbar({
        open: true,
        message: "No location data available to download.",
        severity: "warning",
      });
      return;
    }

    const headers = [
      "Store Name",
      "Store Owner Name",
      "Store Owner Contact Number",
      "Address",
      "City",
      "State",
      "PinCode",
      "Landmark",
      "Zone",
      // "Product Type",
      // "Width",
      // "Height",
      // "Thickness",
      "Location Link",
      "Driver Name",
      "Driver Contact Number",
      "Driver Vehicle Number",
      "SO Name",
      "SO Contact Number",
    ];

    // Merge location and product data (matching index)
    const rows = isJobLocation.map((loc, idx) => {
      const prod = isProduct && isProduct[idx] ? isProduct[idx] : {};
      return [
        loc.storeName || "",
        loc.storeOwnerName || "",
        loc.storeOwnerPhoneNumber || loc.storeOwnerContactNumber || "",
        loc.address || "",
        loc.city || "",
        loc.state || "",
        loc.zipCode || loc.pinCode || "",
        loc.landmark || "",
        loc.zone || "",
        // prod.productType || "",
        // prod.productWidth !== undefined ? prod.productWidth : "",
        // prod.productHeight !== undefined ? prod.productHeight : "",
        // prod.productThickness !== undefined ? prod.productThickness : "",
        loc.locationLink || "",
        loc.driverName || "",
        loc.driverPhoneNumber || "",
        loc.driverVehicleNumber || "",
        loc.soName || "",
        loc.soPhoneNumber || "",
      ];
    });

    // Collect unique product types from isProduct array
    let productTypeList = [];
    //  [
    //   ...new Set((isProduct || []).map((p) => p.productType).filter(Boolean)),
    // ];
    // if (!productTypeList.length && formData.productType?.length)
    //   productTypeList = formData.productType;
    // if (!productTypeList.length && PRODUCT_TYPES?.length)
    //   productTypeList = PRODUCT_TYPES;

    downloadExcelForInputLocations(
      formData?.campaignName,
      productTypeList,
      { headers, rows },
      regions,
    );
  };

  // Download blank template
  const handleDownloadTemplate = () => {
    // If we have existing locations, download them
    if (hasLocationData) {
      downloadExistingLocations();
      return;
    }

    // No locations available - show warning
    setSnackbar({
      open: true,
      message: "No location data available to download.",
      severity: "warning",
    });
  };

  return (
    <>
      <div
        className={`bg-white  p-10 rounded-b-lg shadow ${
          formData.productType.length > 0 ? "mx-6 mb-6" : "m-6"
        }`}
      >
        {" "}
        <h3 className="text-gray-400 font-bold mb-4">BRAND INFORMATION</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <Grid item xs={12} md={6} lg={12}>
            <label
              className={`text-[12px] font-[500] ${
                errors.campaignName && "text-[var(--error-color)]"
              }`}
              style={{ marginBottom: "6px", display: "block" }}
            >
              Campaign Name *
            </label>
            <CustomInput
              className="text-xs font-medium leading-6 text-left"
              placeholder="Enter Campaign Name"
              value={formData?.campaignName || ""}
              type="text"
              size="small"
              name="campaignName"
              customcolor="var(--heading-color)"
              fontWeight="500"
              onChange={(e) => {
                if (!e.target.value.startsWith(" ")) {
                  handleInputChange(e);
                }
              }}
              onBlur={(e) => {
                if (saveButtonClicked) {
                  validateForm();
                }
              }}
              error={errors?.campaignName}
              disabled={operation === "view"}
            />
            {errors.campaignName && (
              <FormHelperText
                className="text-[var(--error-color)] text-[12px] mt-3 ml-3"
                sx={{
                  color: "var(--error-color)",
                  marginLeft: 1.5,
                }}
              >
                {errors?.campaignName}
              </FormHelperText>
            )}
          </Grid>
          {operation === "view" ? (
            <Grid item xs={12} md={6} lg={12}>
              <label
                className="text-[12px] font-[500] mb-1.5"
                style={{
                  display: "block",
                  color: errors.organizationName ? "var(--error-color)" : "",
                }}
              >
                Client Name *
              </label>
              <CustomInput
                className="text-xs font-medium leading-6 text-left"
                placeholder="Enter Campaign Name"
                value={selectedCampaign?.organizationId?.name}
                type="text"
                size="small"
                name="organizationName"
                customcolor="var(--heading-color)"
                fontWeight="500"
                disabled={operation === "view"}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={6} lg={12}>
              <label
                className="text-[12px] font-[500]"
                style={{
                  marginBottom: "6px",
                  display: "block",
                  color: errors.organizationName ? "var(--error-color)" : "",
                }}
              >
                Client Name *
              </label>

              <ClientDropdown
                value={formData.organizationName}
                onChange={(val) => {
                  // Update the form data
                  handleInputChange({
                    target: {
                      name: "organizationName",
                      value: val,
                      organizationId:
                        organizationList?.find(
                          (org) => org?.organizationDetail?.name === val,
                        )?.organizationDetail?._id || null,
                    },
                  });
                }}
                onAddNew={onAddNew}
                options={organizationList?.map(
                  (org) => org?.organizationDetail?.name || "",
                )}
                error={!!errors.organizationName}
                helperText={errors?.organizationName}
              />
            </Grid>
          )}
          {operation === "view" ? (
            <Grid item xs={12} md={6} lg={12}>
              <label
                className="text-[12px] font-[500]"
                style={{
                  marginBottom: "6px",
                  display: "block",
                  color: errors.organizationName ? "var(--error-color)" : "",
                }}
              >
                Campaign Owner Email *
              </label>
              <CustomInput
                className="text-xs font-medium leading-6 text-left"
                placeholder="Enter Campaign Name"
                value={selectedCampaign?.campaignOwnerId?.emailAddress}
                type="text"
                size="small"
                name="campaignOwnerEmail"
                customcolor="var(--heading-color)"
                fontWeight="500"
                disabled={operation === "view"}
              />
            </Grid>
          ) : (
            <Grid item xs={12} md={6} lg={12}>
              <GlobalDropdownField
                name="emailAddress"
                label="Campaign Owner Email *"
                value={formData?.emailAddress}
                onChange={handleInputChange}
                onBlur={(e) => {
                  if (saveButtonClicked) {
                    validateForm();
                  }
                }}
                placeholder="Select Owner Email Address"
                options={team?.map((member) => ({
                  value: member?.emailAddress,
                  label: member?.emailAddress,
                }))}
                variant="enquirySelect"
                error={errors.emailAddress}
                errorMessage={errors.emailAddress}
                renderValueFn={(selected) =>
                  selected ? (
                    <span style={{ color: "var(--heading-color)" }}>
                      {selected}
                    </span>
                  ) : (
                    <span
                      style={{
                        color: "var(--listdown-color)",
                        fontSize: "1rem",
                      }}
                    >
                      Select Owner Email Address
                    </span>
                  )
                }
              />
            </Grid>
          )}

          <Grid item xs={12} md={6} lg={12}>
            <label
              className="text-[12px] font-[500]"
              style={{
                marginBottom: "6px",
                display: "block",
                color: errors.campaignOwnerName ? "var(--error-color)" : "",
              }}
            >
              Campaign Owner Name *
            </label>

            <CustomInput
              className="text-xs font-medium leading-6 text-left h-30 "
              placeholder="Enter Owner Name"
              type="text"
              size="small"
              name="campaignOwnerName"
              value={formData?.campaignOwnerName || ""}
              customcolor="var(--heading-color)"
              fontWeight="500"
              error={errors.campaignOwnerName}
              onChange={handleInputChange}
              onBlur={() => {
                if (saveButtonClicked) {
                  validateForm();
                }
              }}
              disabled
            />

            {errors?.campaignOwnerName && (
              <FormHelperText
                className="text-[var(--error-color)] text-[12px] -mt-1 ml-3"
                sx={{
                  color: "var(--error-color)",
                  marginTop: 0.3,
                  marginLeft: 1.5,
                }}
              >
                {errors?.campaignOwnerName}
              </FormHelperText>
            )}
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <label
              className="text-[12px] font-[500]"
              style={{
                marginBottom: "6px",
                display: "block",
                color: errors.phoneNumber ? "var(--error-color)" : "",
              }}
            >
              Campaign Owner Contact Number *
            </label>

            <CustomInput
              className="text-xs font-medium leading-6 text-left h-30 "
              placeholder="Enter Contact Number"
              type="tel"
              size="small"
              name="phoneNumber"
              value={formData?.phoneNumber}
              customcolor="var(--heading-color)"
              fontWeight="500"
              error={errors?.phoneNumber}
              onChange={(e) => {
                // Only allow digits (0-9)
                const numericValue = e.target.value.replace(/\D/g, "");

                // Update the input (but prevent leading spaces)
                if (!e.target.value.startsWith(" ")) {
                  handleInputChange({
                    ...e,
                    target: {
                      ...e.target,
                      value: numericValue,
                    },
                  });
                }
              }}
              onKeyDown={(e) => {
                // Allow: backspace, delete, tab, escape, enter, arrows
                if (
                  [
                    "Backspace",
                    "Delete",
                    "Tab",
                    "Escape",
                    "Enter",
                    "ArrowLeft",
                    "ArrowRight",
                  ].includes(e.key)
                ) {
                  return; // Let these keys work normally
                }

                // Prevent non-numeric keys
                if (e.key.match(/\D/) && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                }
              }}
              onBlur={() => {
                if (saveButtonClicked) {
                  validateForm();
                }
              }}
              disabled
            />
            {errors?.phoneNumber && (
              <FormHelperText
                className="text-[var(--error-color)] text-[12px] -mt-1 ml-3"
                sx={{
                  color: "var(--error-color)",
                  marginTop: 0.3,
                  marginLeft: 1.5,
                }}
              >
                {errors?.phoneNumber}
              </FormHelperText>
            )}
          </Grid>
          {Array.isArray(formData.categories) && (
            <Grid item xs={12} md={6} lg={12}>
              <label className="text-[12px] font-[500] mb-1">Category *</label>

              <FormControl fullWidth size="small">
                <Select
                  multiple
                  displayEmpty
                  value={formData.categories || []}
                  disabled={operation === "view"}
                  onChange={(e) =>
                    onCategoryChange(
                      typeof e.target.value === "string"
                        ? e.target.value.split(",")
                        : e.target.value,
                    )
                  }
                  renderValue={(selected) => {
                    if (selected.length === 0) return "Choose Categories";
                    if (selected.length > 2)
                      return `No of Categories ${selected.length}`;
                    return selected.join(", ");
                  }}
                >
                  {categories.map((cat, i) => (
                    <MenuItem key={i} value={cat.category}>
                      <Checkbox
                        checked={formData?.categories.includes(cat?.category)}
                      />
                      <ListItemText primary={cat?.category} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6} lg={12}>
            <label className="text-[12px] font-[500] mb-1">
              Product Type *
            </label>

            <FormControl fullWidth size="small">
              {(() => {
                const resolvedProductTypes =
                  !Array.isArray(formData.categories) ||
                  formData.categories.length === 0
                    ? PRODUCT_TYPES
                    : productTypes;

                return (
                  <Select
                    multiple
                    displayEmpty
                    value={formData.productType || []}
                    disabled={operation === "view"}
                    onChange={(e) => {
                      const value =
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : e.target.value;

                      setFormData((prev) => ({
                        ...prev,
                        productType: value,
                      }));
                    }}
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0)
                        return "Choose Product Types";
                      if (selected.length > 2)
                        return `No of Product ${selected.length}`;
                      return selected.join(", ");
                    }}
                  >
                    {/* 🔄 Loader ONLY when categories selected */}
                    {loadingProductTypes &&
                    Array.isArray(formData.categories) &&
                    formData.categories.length > 0 ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : (
                      resolvedProductTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          <Checkbox
                            checked={(formData.productType || []).includes(
                              type,
                            )}
                          />
                          <ListItemText primary={type} />
                        </MenuItem>
                      ))
                    )}
                  </Select>
                );
              })()}
            </FormControl>

            {errors.productType && (
              <div className="text-xs mt-1 ml-1 text-red-500">
                {errors.productType}
              </div>
            )}
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <label
              className="text-[12px] font-[500]"
              style={{
                marginBottom: "6px",
                display: "block",
                color: errors.turnAroundTime ? "var(--error-color)" : "",
              }}
            >
              {`Turn Around Time (Days) *`}
            </label>

            <TextField
              className="text-xs font-medium leading-6 text-left w-full"
              placeholder="Enter Number Of Days"
              type="number"
              value={formData?.turnAroundTime || ""}
              size="small"
              name="turnAroundTime"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: errors?.turnAroundTime
                      ? "var(--error-color)"
                      : "var(--listdown-color)",
                  },
                  "&:hover fieldset": {
                    borderColor: errors?.turnAroundTime
                      ? "var(--error-color)"
                      : "var(--listdown-color)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: errors?.turnAroundTime
                      ? "var(--error-color)"
                      : "var(--primary-color)",
                  },
                },
              }}
              onChange={handleInputChange}
              disabled={operation === "view"}
              inputProps={{
                maxLength: 6, // Limits input to 6 digits
                onWheel: (e) => {
                  e.preventDefault(); // Prevents scrolling from changing the value
                  e.target.blur(); // Optional: Removes focus to prevent accidental changes
                },
                onKeyDown: (e) => {
                  // Blocks "e", "+", "-", "." in numeric input
                  if (["e", "E", "+", "-", "."].includes(e.key)) {
                    e.preventDefault();
                  }
                },
              }}
            />

            {errors?.turnAroundTime && (
              <FormHelperText
                className="text-[var(--error-color)] text-[12px] -mt-1 ml-3"
                sx={{
                  color: "var(--error-color)",
                  marginTop: 0.3,
                  marginLeft: 1.5,
                }}
              >
                {errors?.turnAroundTime}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} md={12} lg={12}>
            <label
              className="text-[12px] font-[500]"
              style={{
                marginBottom: "6px",
                display: "block",
              }}
            >
              Associated Brands
            </label>

            <TextField
              className="text-xs font-medium leading-6 text-left w-full"
              placeholder="Enter List Of Brands (optional)"
              type="text"
              value={formData?.associatedBrands || ""}
              size="small"
              name="associatedBrands"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "var(--listdown-color)",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--listdown-color)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--primary-color)",
                  },
                },
              }}
              onChange={handleInputChange}
              disabled={operation === "view"}
            />
          </Grid>
        </div>
      </div>
      <div className="grid grid-cols-1  gap-8 m-6">
        {/* UPLOAD DOCUMENTS */}
        <div className="col-span-12 md:col-span-12 bg-white p-10 rounded-lg shadow">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-gray-400 font-bold">UPLOAD DOCUMENTS</h3>

            <div className="flex justify-between gap-2">
              {/* <CustomButton
                type="Submit"
                bgcolor={formData.locationsUploaded && "var(--primary-color)"}
                fontWeight="600"
                className="h-10"
                disabled={formData.locationsUploaded ? false : true}
                text={"Store Route"}
                onClick={(e) => {
                  setDistanceModal(true);
                }}
              /> */}
              <CustomButton
                type="Submit"
                bgcolor={hasLocationData && "var(--primary-color)"}
                fontWeight="600"
                disabled={!hasLocationData}
                className="h-10"
                text={"View Stores Map"}
                onClick={(e) => {
                  setLocationMapModal(true);
                }}
              />
              <CustomButton
                type="Submit"
                bgcolor={hasLocationData && "var(--primary-color)"}
                fontWeight="600"
                disabled={!hasLocationData}
                className="h-10"
                text={"View Locations"}
                onClick={(e) => {
                  setIsLocationModal(true);
                }}
              />
              {isLocationModal && (
                <LocationsTablesDialog
                  isLocationModal={isLocationModal}
                  setIsLocationModal={setIsLocationModal}
                  locations={isJobLocation}
                  products={isProduct}
                />
              )}
              {locationMapModal && (
                <AllLocationMapModal
                  locationMapModal={locationMapModal}
                  setLocationMapModal={setLocationMapModal}
                  locations={isJobLocation}
                  products={isProduct}
                />
              )}
              {distanceModal && (
                // <CalculateStoreDistance
                //   distanceModal={distanceModal}
                //   setDistanceModal={setDistanceModal}
                //   locations={isJobLocation}
                // />
                // <MapboxDistance
                //   distanceModal={distanceModal}
                //   setDistanceModal={setDistanceModal}
                //   locations={isJobLocation}
                // />
                <NextBillionDistance
                  distanceModal={distanceModal}
                  setDistanceModal={setDistanceModal}
                  locations={isJobLocation}
                  products={isProduct}
                />
                // <OptimizedRouteMap
                //   distanceModal={distanceModal}
                //   setDistanceModal={setDistanceModal}
                //   locations={isJobLocation}
                // />
              )}

              <Tooltip
                title={
                  !hasLocationData
                    ? "No data available"
                    : "Download Excel file of locations"
                }
                placement="top"
              >
                <span>
                  <CustomButton
                    type="Submit"
                    bgcolor={hasLocationData && "var(--primary-color)"}
                    fontWeight="600"
                    className="h-10"
                    text={"Download Locations"}
                    onClick={handleDownloadTemplate}
                    startIcon={
                      <DownloadIcon
                        color={!hasLocationData ? "black" : "white"}
                      />
                    }
                    disabled={!hasLocationData}
                    sx={{
                      opacity: !hasLocationData ? 0.6 : 1,
                      cursor: !hasLocationData ? "not-allowed" : "pointer",
                      "& .MuiButton-startIcon": {
                        color: !hasLocationData ? "black" : "white",
                      },
                    }}
                  />
                </span>
              </Tooltip>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {operation != "view" && (
              <div className="flex flex-col items-center justify-evenly border-2 border-dashed border-gray-300 rounded-md p-2 max-w-40 h-40 text-center cursor-pointer hover:border-blue-500 transition">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2">
                  <img src={XLS} alt="XLS" className="w-6 h-6" />
                </div>
                {!excelLoading ? (
                  <div
                    className="text-blue-600 font-medium text-xs"
                    onClick={handleIconClick}
                  >
                    <label className="cursor-pointer">
                      Upload Location Data{" "}
                    </label>
                    <input
                      type="file"
                      ref={locationFileInputRef}
                      onChange={async (e) => {
                        setExcelLoading(true);

                        let response = await handleFileUploadForLocations(
                          e,
                          setSnackbar,
                          setExcelLoading,
                          true,
                          formData.productType,
                        );
                        if (response?.success) {
                          setJobLocations(response?.jobLocations);
                          setProductsLocation(response?.products);

                          setSnackbar({
                            open: true,
                            message: "File Uploaded Successfully",
                            severity: "success",
                          });
                          setExcelLoading(false);
                          setFormData((prev) => ({
                            ...prev,
                            locationsUploaded: response.data,
                          }));
                        } else {
                          setExcelLoading(false);
                        }
                      }}
                      style={{
                        display: "none",
                      }}
                      accept=".xlsx"
                    />
                  </div>
                ) : (
                  <CircularProgress />
                )}

                <p className="text-xs text-black-600">(Max. File size: 2 MB)</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="w-full mt-4 space-y-2">
              {formData.document.length > 0
                ? formData.document.map((doc, index) => {
                    const fileUrl = doc.url || doc;
                    const fileName = decodeURIComponent(
                      doc.name || fileUrl.split("/").pop(),
                    );
                    const fileExtension = fileName
                      .split(".")
                      .pop()
                      .toLowerCase();
                    const fileSize =
                      doc.size ||
                      (doc.file?.size ?? (doc.dummy ? 204800 : null)); // fallback

                    const formattedSize = fileSize
                      ? `${Math.round(fileSize / 1024)} KB`
                      : "—";

                    const extensionIcon =
                      {
                        pdf: PDF,
                        jpg: JPG,
                        jpeg: JPG,
                        png: PNG,
                        xls: XLS,
                        xlsx: XLS,
                        doc: DOC,
                        docx: DOC,
                      }[fileExtension] || DOC;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between border-2 border-gray-200 rounded px-4 py-2 bg-white"
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={extensionIcon}
                            alt={fileExtension}
                            width={40}
                            height={40}
                            className="cursor-pointer"
                            onClick={() =>
                              !doc.dummy && handleOpenModal(fileUrl)
                            }
                          />
                          <div className="flex flex-col max-w-[200px] truncate">
                            <span className="text-sm truncate" title={fileName}>
                              {fileName}
                            </span>
                          </div>
                        </div>

                        {!doc.dummy && operation !== "view" && (
                          <button
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => handleRemoveDocument(index)}
                          >
                            <DeleteOutlineOutlined fontSize="small" />
                          </button>
                        )}
                      </div>
                    );
                  })
                : ""}
            </div>

            <div className="w-full mt-4 space-y-2">
              {formData.locationsUploaded && (
                <div className="flex items-center justify-between border-2 border-gray-200 rounded px-4 py-2 bg-white">
                  <div className="flex items-center gap-3">
                    <img
                      src={XLS}
                      alt={formData.locationsUploaded?.type}
                      width={40}
                      height={40}
                      className="cursor-pointer"
                    />

                    <div className="flex flex-col max-w-[200px] truncate">
                      <span
                        className="text-sm truncate"
                        title={formData.locationsUploaded?.name}
                      >
                        {formData.locationsUploaded.slice(70, 100)}
                      </span>
                    </div>
                  </div>
                  {formData?.locationsUploaded && (
                    <CheckCircleIcon className="text-green-500 w-6 h-6" />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {localStorage.getItem("role") == 3 ||
        localStorage.getItem("role") == 0 ? (
          <div></div>
        ) : (
          <div className="col-span-12 md:col-span-12 bg-white p-10 rounded-lg shadow">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-gray-600 font-bold text-lg">
                Cost Calculator Table
              </h3>
            </div>

            <div className="max-h-[400px] overflow-y-auto overflow-x-auto rounded-2xl shadow-md">
              <table className={styles.campaignTable}>
                <thead>
                  <tr>
                    <th className={styles.campaignTableHeader}>
                      {`Product Name`}
                    </th>
                    {regions.length > 0 ? (
                      regions.map((region, index) => {
                        return (
                          <th
                            key={index}
                            className={styles.campaignTableHeader}
                          >
                            {`Zone: ${region?.regionName}`}
                          </th>
                        );
                      })
                    ) : (
                      <th className={styles.campaignTableHeader}>
                        {`Total Cost`}
                      </th>
                    )}
                    {/* {localStorage.getItem("role") != "1" && (
                      <th className={styles.campaignTableHeader}>
                        Cost Margin
                      </th>
                    )} */}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {formData?.products?.length > 0 ? (
                    formData?.products?.map((ele, idx) => (
                      <tr key={idx} className={styles.campaignTableRow}>
                        {/* Product Name */}
                        <td className={styles.campaignTableCell}>
                          {ele?.productType}
                        </td>

                        {/* Total Cost */}
                        {regions?.length > 0 ? (
                          regions?.map((region, index) => {
                            const cost = getRegionCost(ele, region.regionName);

                            const raw =
                              ele?.costCalculator?.regionWiseCosts?.find(
                                (r) =>
                                  String(r.regionName) ===
                                  String(region.regionName),
                              )?.totalCostOfProduct;

                            const shown =
                              raw !== undefined ? raw : (cost ?? "");
                            return (
                              <td
                                key={index}
                                className={styles.campaignTableCell}
                              >
                                {isViewMode ? (
                                  cost != null ? (
                                    Number(cost).toFixed(2)
                                  ) : (
                                    ""
                                  )
                                ) : (
                                  <input
                                    type="text"
                                    value={shown}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    onChange={(e) => {
                                      const value = e.target.value;

                                      // Allow empty value while typing
                                      if (value === "") {
                                        handleRegionCostChange(idx, region, "");
                                        return;
                                      }

                                      // Allow positive numbers with up to 2 decimals
                                      if (/^\d*\.?\d{0,2}$/.test(value)) {
                                        handleRegionCostChange(
                                          idx,
                                          region,
                                          value,
                                        );
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const value = Number(e.target.value);

                                      if (!value || value <= 0) {
                                        removeRegionCost(idx, region);
                                      } else {
                                        handleRegionCostChange(
                                          idx,
                                          region,
                                          value,
                                        );
                                      }
                                    }}
                                    className="w-36 border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                  />
                                )}
                              </td>
                            );
                          })
                        ) : isViewMode ? (
                          ele?.costCalculator?.totalCostOfProduct != null ? (
                            Number(
                              ele.costCalculator.totalCostOfProduct,
                            ).toFixed(2)
                          ) : (
                            ""
                          )
                        ) : (
                          <input
                            type="text"
                            value={
                              ele?.costCalculator?.totalCostOfProduct ?? ""
                            }
                            onWheel={(e) => e.currentTarget.blur()}
                            onChange={(e) => {
                              const value = e.target.value;

                              // Allow empty value while typing
                              if (value === "") {
                                handleCostChange(idx, "totalCostOfProduct", "");
                                return;
                              }

                              // Allow only numbers with up to 2 decimals
                              if (/^\d*\.?\d{0,2}$/.test(value)) {
                                handleCostChange(
                                  idx,
                                  "totalCostOfProduct",
                                  value,
                                );
                              }
                            }}
                            onBlur={(e) => {
                              const value = Number(e.target.value);

                              if (!value || value <= 0) {
                                handleCostChange(idx, "totalCostOfProduct", 1);
                              }
                            }}
                            className="w-36 border rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                          />
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={localStorage.getItem("role") !== "1" ? 3 : 2}
                        className="px-6 py-4 text-gray-700 text-center font-medium"
                      >
                        Please Select at least one product to show
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default EnquiryHeader;
